import { _MiscMessages, ConfigSystemParams, MdmDashboardWidgetTitles, MdmDeviceClass } from 'src/app/core/consts';
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { catchError, combineLatest, debounceTime, distinctUntilChanged, Observable, of, startWith, Subject, switchMap, takeUntil } from 'rxjs';
import { ChartConfigService } from 'src/app/core/services/utelize/general/chart-config-service';
import { ConfigSystemService } from 'src/app/core/services/utelize/system/config-system/config-system-service';
import { ExceptionsService } from 'src/app/core/services/utelize/general/exceptions-service';
import { IntegerResultAndStringResultDTO } from 'src/app/core/interfaces/general/integer-result-and-string-result';
import { MdmDevicesIntuneStatsService } from 'src/app/core/services/utelize/system/device-management/mdm-devices-intune-stats-service';
import { OurDate } from 'src/app/core/interfaces/general/our-date';
import { mdmUserDomainsDisplayText } from 'src/app/core/functions-generic/functions-generic-mdm';
import { populateOurDate } from 'src/app/core/functions-generic/functions-generic-date';
import { TippyToolTipService } from 'src/app/core/services/utelize/general/tippy-tool-tip-service';
import { WidgetRowChartType } from '../widget-row-chart-type.model';
import { WidgetService } from 'src/app/core/services/utelize/general/widget-service';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-widget-row-mdm-devices-01',
  templateUrl: './widget-row-mdm-devices-01.component.html',
  styleUrls: ['./widget-row-mdm-devices-01.component.scss'],
  providers: [TippyToolTipService]
})

export class WidgetRowMdmDevices01Component implements AfterViewInit, OnChanges, OnDestroy, OnInit {

  @Input() actionButton0Show  : boolean | undefined;
  @Input() actionButton0Text  : string  | undefined;
  @Input() allowContainerClose: boolean | undefined;
  @Input() containerTitle     : string  | undefined;
  @Input() deviceClass        : string  | undefined;
  @Input() marginBottom       : string  | undefined="mb-0";
  @Input() previousSync       : boolean | undefined=false;
  @Input() useContainer       : boolean | undefined;
  @Input() showTitleHintText  : boolean | undefined;
  @Input() userDomains        : string  | undefined;
    
  @Output() actionButton0Click: EventEmitter<void                                                            >=new EventEmitter<void                                                            >();
  @Output() containerClosed   : EventEmitter<void                                                            >=new EventEmitter<void                                                            >();
  @Output() legendClick       : EventEmitter<{chartName: string, labelText: string, excludeLabels?: string[]}>=new EventEmitter<{chartName: string, labelText: string, excludeLabels?: string[]}>();
  @Output() userDomainsClear  : EventEmitter<void                                                            >=new EventEmitter<void                                                            >();

  get isOtherClass     () { return this.deviceClass===MdmDeviceClass.other                            ; }
  get userDomainsText  () { return mdmUserDomainsDisplayText(this.exceptionsService, this.userDomains); }
  get visibleChartCount() { return this.isOtherClass ? 4 : 6                                          ; }
    
  get countsInLegend() { return this.configSystemService.getValueAsBoolean(ConfigSystemParams.WidgetRow_IncludeCountsInLegend_MdmDevices01, false); }
  get pollingWidgets() { return this.configSystemService.getValueAsBoolean(ConfigSystemParams.WidgetRow_Polling_MdmDevices01,               false); }
   
  get maxChartHeight  () { return this.configSystemService.getValueAsInteger(ConfigSystemParams.WidgetRow_MaxChartHeight_MdmDevices01,   333); } 
  get maxWidgetsPerRow() { return this.configSystemService.getValueAsInteger(ConfigSystemParams.WidgetRow_MaxWidgetsPerRow_MdmDevices01, 4  ); }

  private filterCompanyID  : number  | undefined;
  private filterDate       : OurDate | undefined=populateOurDate(this.exceptionsService, Date.now());
  private latestDataByChart: Map<string, IntegerResultAndStringResultDTO[]>=new Map<string, IntegerResultAndStringResultDTO[]>();
  private legendShowAllSet : Set<string>=new Set<string>();
  
  private destroy$  =new Subject<void>();
  private selection$=new Subject<{ companyId: number; date: { year: number; month: number; day: number }; refreshToken?: number; }>();
  
  private readonly legendCollapseThreshold: number=10;
  private readonly legendCollapseTopCount : number=5;
  
  public devicesByManagementStateChart!                   : WidgetRowChartType;
  public devicesByManagementStateCounts                   : number[]=[];
  public devicesByManufacturerChart!                      : WidgetRowChartType;
  public devicesByManufacturerCounts                      : number[]=[];
  public devicesByOperatingSystemChart!                   : WidgetRowChartType;
  public devicesByOperatingSystemCounts                   : number[]=[];
  public devicesByOperatingSystemCompliant_IntuneChart!   : WidgetRowChartType;
  public devicesByOperatingSystemCompliant_IntuneCounts   : number[]=[];
  public devicesByOperatingSystemCompliant_OursChart!     : WidgetRowChartType;
  public devicesByOperatingSystemCompliant_OursCounts     : number[]=[];
  public devicesByOperatingSystemCompliant_OursMinorChart!: WidgetRowChartType;
  public devicesByOperatingSystemCompliant_OursMinorCounts: number[]=[];
  public expandContainer                                  : boolean=true;
  public loadingCharts                                    : Set<string>=new Set<string>(this.allChartNames());
  public syncInfoText                                     : string="";
  public visibleContainer                                 : boolean=true;
  
  public readonly chartTitles=MdmDashboardWidgetTitles;

  constructor(
    private changeDetectorRef           : ChangeDetectorRef,
    private configSystemService         : ConfigSystemService,
    private exceptionsService           : ExceptionsService,
    private mdmDevicesIntuneStatsService: MdmDevicesIntuneStatsService,
    private tippyToolTipService         : TippyToolTipService,
    private widgetService               : WidgetService,

    public chartConfigService: ChartConfigService
  ) {

  }

  public ngOnInit(): void {
    try {
      this.initSelectionStream();
      
      if (!this.containerTitle || this.containerTitle.trim()=="")
        this.containerTitle=_MiscMessages.TitleNotSet;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  public ngOnChanges(changes: SimpleChanges): void {
    try {
      if (((changes['deviceClass'] && !changes['deviceClass'].firstChange)
           || (changes['userDomains'] && !changes['userDomains'].firstChange)
          ) && (this.filterCompanyID ?? -1)!=-1)
        this.refreshWidgets();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public ngAfterViewInit(): void {
    try {

    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public ngOnDestroy(): void {
    try {
      this.tippyToolTipService.chartTippyDestroy();

      this.destroy$.next();
      this.destroy$.complete();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public refreshWidgets(): void {
    try {
      const companyId=this.filterCompanyID ?? -1;
      const date     ={
        year : <number>(this.filterDate?.year  ?? 1),
        month: <number>(this.filterDate?.month ?? 1),
        day  : <number>(this.filterDate?.day   ?? 1),
      };
  
      this.selection$.next({ companyId, date, refreshToken: Date.now() });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  private initSelectionStream(): void {
    try {
      this.selection$
      .pipe(
        debounceTime(150),
        distinctUntilChanged((a, b) =>
          a.companyId   ===b.companyId  &&
          a.date.year   ===b.date.year  &&
          a.date.month  ===b.date.month &&
          a.date.day    ===b.date.day   &&
          a.refreshToken===b.refreshToken
        ),
        switchMap(selected => {
          const companyId=selected.companyId;
          const year     =selected.date.year;
          const month    =selected.date.month;
          const day      =selected.date.day;
          
          const safeStart=<T>(observable: Observable<T>, fallback: T) =>
            observable.pipe(
              startWith(<T | null>null),
              catchError(err => {
                this.exceptionsService.handleException(new Error(this.exceptionsService.formatHttpError(err)));
                return of(fallback);
              })
            );
          
          const devicesByManagementStateSource=this.pollingWidgets
                                               ? this.widgetService.getIntuneDevicesByManagementStateUpdates(companyId, this.previousSync ?? false, this.deviceClass, this.userDomains)
                                               : this.widgetService.getIntuneDevicesByManagementState       (companyId, this.previousSync ?? false, this.deviceClass, this.userDomains);

          const devicesByManufacturerSource=this.pollingWidgets
                                            ? this.widgetService.getIntuneDevicesByManufacturerUpdates(companyId, this.previousSync ?? false, this.deviceClass, this.userDomains)
                                            : this.widgetService.getIntuneDevicesByManufacturer       (companyId, this.previousSync ?? false, this.deviceClass, this.userDomains);

          const devicesByOperatingSystemCompliant_IntuneSource=this.pollingWidgets
                                                               ? this.widgetService.getIntuneDevicesByOperatingSystemCompliant_IntuneUpdates(companyId, this.previousSync ?? false, this.deviceClass, this.userDomains)
                                                               : this.widgetService.getIntuneDevicesByOperatingSystemCompliant_Intune       (companyId, this.previousSync ?? false, this.deviceClass, this.userDomains);

          const devicesByOperatingSystemCompliant_OursSource=this.pollingWidgets
                                                             ? this.widgetService.getIntuneDevicesByOperatingSystemCompliant_OursUpdates(companyId, this.previousSync ?? false, this.deviceClass, this.userDomains)
                                                             : this.widgetService.getIntuneDevicesByOperatingSystemCompliant_Ours       (companyId, this.previousSync ?? false, this.deviceClass, this.userDomains);

          const devicesByOperatingSystemCompliant_OursMinorSource=this.pollingWidgets
                                                                  ? this.widgetService.getIntuneDevicesByOperatingSystemCompliant_OursMinorUpdates(companyId, this.previousSync ?? false, this.deviceClass, this.userDomains)
                                                                  : this.widgetService.getIntuneDevicesByOperatingSystemCompliant_OursMinor       (companyId, this.previousSync ?? false, this.deviceClass, this.userDomains);

          const devicesByOperatingSystemSource=this.pollingWidgets
                                               ? this.widgetService.getIntuneDevicesByOperatingSystemUpdates(companyId, this.previousSync ?? false, this.deviceClass, this.userDomains)
                                               : this.widgetService.getIntuneDevicesByOperatingSystem       (companyId, this.previousSync ?? false, this.deviceClass, this.userDomains);

          return combineLatest({
            devicesByManagementState                   : safeStart(devicesByManagementStateSource,                    []),
            devicesByManufacturer                      : safeStart(devicesByManufacturerSource,                       []),
            devicesByOperatingSystem                   : safeStart(devicesByOperatingSystemSource,                    []),
            devicesByOperatingSystemCompliant_Intune   : safeStart(devicesByOperatingSystemCompliant_IntuneSource,    []),
            devicesByOperatingSystemCompliant_Ours     : safeStart(devicesByOperatingSystemCompliant_OursSource,      []),
            devicesByOperatingSystemCompliant_OursMinor: safeStart(devicesByOperatingSystemCompliant_OursMinorSource, []),
          });
          // .pipe(
          //   catchError((error) => {
          //     this.exceptionsService.handleException(new Error(this.exceptionsService.formatHttpError(error)));
          //     return EMPTY; //👈 CRN_20251224 - If one fails, nothing is returned
          //   })
          // );
        }),
        takeUntil(this.destroy$)
      )
      .subscribe(result => {
        this.applyDevicesByManagementState                   (result.devicesByManagementState);
        this.applyDevicesByManufacturer                      (result.devicesByManufacturer);
        this.applyDevicesByOperatingSystem                   (result.devicesByOperatingSystem);
        this.applyDevicesByOperatingSystemCompliant_Intune   (result.devicesByOperatingSystemCompliant_Intune);
        this.applyDevicesByOperatingSystemCompliant_Ours     (result.devicesByOperatingSystemCompliant_Ours);
        this.applyDevicesByOperatingSystemCompliant_OursMinor(result.devicesByOperatingSystemCompliant_OursMinor);
        this.changeDetectorRef.markForCheck();
      });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
    
  public emitSelection(filterCompanyID: number | undefined, filterDate: OurDate | undefined): void {
    try {
      this.devicesByManagementStateChart                   =undefined!;
      this.devicesByManufacturerChart                      =undefined!;
      this.devicesByOperatingSystemChart                   =undefined!;
      this.devicesByOperatingSystemCompliant_IntuneChart   =undefined!;
      this.devicesByOperatingSystemCompliant_OursChart     =undefined!;
      this.devicesByOperatingSystemCompliant_OursMinorChart=undefined!;
      this.filterCompanyID                                 =filterCompanyID;
      this.filterDate                                      =filterDate;
      this.loadingCharts                                   =new Set<string>(this.allChartNames());

      const companyId=this.filterCompanyID ?? -1;
      const date     ={
        year : <number>(this.filterDate?.year  ?? 1),
        month: <number>(this.filterDate?.month ?? 1),
        day  : <number>(this.filterDate?.day   ?? 1),
      };
      
      this.selection$.next({ companyId, date });

      if (this.previousSync)
        this.getSyncedByInfo_Previous();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public getSyncedByInfo_Previous(): void {
    try {
      const companyID: number=this.filterCompanyID ?? 0;

      if (companyID<=0) {
        this.syncInfoText="";
        return;
      }

      this.mdmDevicesIntuneStatsService.getSyncedByInfo_Previous(companyID)
      .pipe(
        catchError((error) => {
          this.exceptionsService.handleException(new Error(this.exceptionsService.formatHttpError(error)));
          return of(null);
        })
      )
      .subscribe({
        next: data => {
          if (!data?.dateResult) {
            this.syncInfoText="Unsynced";
            this.changeDetectorRef.markForCheck();
            return;
          }

          const date     =new Date(data.dateResult);
          const pad      =(n: number) => n.toString().padStart(2, '0');
          const formatted=`${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} ` +
                          `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;

          this.syncInfoText=data?.stringResult
                            ? `${formatted} by ${data.stringResult}`
                            : `${formatted}`;
          this.changeDetectorRef.markForCheck();
        },
        error: error => {
          this.exceptionsService.handleException(new Error(error));
        }
      });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private applyDevicesByManagementState(data: IntegerResultAndStringResultDTO[] | null) {
    try {
      if (data===null)
        return;

      if (this.latestDataByChart.get(MdmDashboardWidgetTitles.devicesByManagementState)===data)
        return;

      this.latestDataByChart.set(MdmDashboardWidgetTitles.devicesByManagementState, data);
      this.buildAndApplyPieChart(MdmDashboardWidgetTitles.devicesByManagementState,
                                 data,
                                 chart => this.devicesByManagementStateChart=chart);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private applyDevicesByManufacturer(data: IntegerResultAndStringResultDTO[] | null) {
    try {
      if (data===null)
        return;

      if (this.latestDataByChart.get(MdmDashboardWidgetTitles.devicesByManufacturer)===data)
        return;

      this.latestDataByChart.set(MdmDashboardWidgetTitles.devicesByManufacturer, data);
      this.buildAndApplyPieChart(MdmDashboardWidgetTitles.devicesByManufacturer,
                                 data,
                                 chart => this.devicesByManufacturerChart=chart);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private applyDevicesByOperatingSystem(data: IntegerResultAndStringResultDTO[] | null) {
    try {
      if (data===null)
        return;

      if (this.latestDataByChart.get(MdmDashboardWidgetTitles.devicesByOperatingSystem)===data)
        return;

      this.latestDataByChart.set(MdmDashboardWidgetTitles.devicesByOperatingSystem, data);
      this.buildAndApplyPieChart(MdmDashboardWidgetTitles.devicesByOperatingSystem,
                                 data,
                                 chart => this.devicesByOperatingSystemChart=chart);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private applyDevicesByOperatingSystemCompliant_Intune(data: IntegerResultAndStringResultDTO[] | null) {
    try {
      if (data===null)
        return;

      if (this.latestDataByChart.get(MdmDashboardWidgetTitles.devicesByOperatingSystemCompliant_Intune)===data)
        return;

      this.latestDataByChart.set(MdmDashboardWidgetTitles.devicesByOperatingSystemCompliant_Intune, data);
      this.buildAndApplyPieChart(MdmDashboardWidgetTitles.devicesByOperatingSystemCompliant_Intune,
                                 data,
                                 chart => this.devicesByOperatingSystemCompliant_IntuneChart=chart);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private applyDevicesByOperatingSystemCompliant_Ours(data: IntegerResultAndStringResultDTO[] | null) {
    try {
      if (data===null)
        return;

      if (this.latestDataByChart.get(MdmDashboardWidgetTitles.devicesByOperatingSystemCompliant_Ours)===data)
        return;

      this.latestDataByChart.set(MdmDashboardWidgetTitles.devicesByOperatingSystemCompliant_Ours, data);
      this.buildAndApplyPieChart(MdmDashboardWidgetTitles.devicesByOperatingSystemCompliant_Ours,
                                 data,
                                 chart => this.devicesByOperatingSystemCompliant_OursChart=chart);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private applyDevicesByOperatingSystemCompliant_OursMinor(data: IntegerResultAndStringResultDTO[] | null) {
    try {
      if (data===null)
        return;

      if (this.latestDataByChart.get(MdmDashboardWidgetTitles.devicesByOperatingSystemCompliant_OursMinor)===data)
        return;

      this.latestDataByChart.set(MdmDashboardWidgetTitles.devicesByOperatingSystemCompliant_OursMinor, data);
      this.buildAndApplyPieChart(MdmDashboardWidgetTitles.devicesByOperatingSystemCompliant_OursMinor,
                                 data,
                                 chart => this.devicesByOperatingSystemCompliant_OursMinorChart=chart);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private buildAndApplyPieChart(chartName: string,
                                data     : IntegerResultAndStringResultDTO[],
                                assign   : (chart: WidgetRowChartType) => void): void {
    try {
      const collapseEnabled=data.length > this.legendCollapseThreshold && !this.legendShowAllSet.has(chartName);

      this.applyPieChart(chartName,
                         () => this.chartConfigService.createPieChart<IntegerResultAndStringResultDTO>
                                                                     (chartName,
                                                                      "pie",
                                                                      data,
                                                                      x => x.stringResult,
                                                                      x => x.integerResult,
                                                                      this.maxChartHeight,
                                                                      this.countsInLegend,
                                                                      (target, html) => this.tippyToolTipService.chartTippyShow(target, html),
                                                                      () => this.tippyToolTipService.chartTippyHide(),
                                                                      (chartRef, rawLabel) => this.onLegendClick(chartRef, rawLabel),
                                                                      undefined,
                                                                      { enabled: collapseEnabled, topCount: this.legendCollapseTopCount }),
                         assign);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private applyPieChart(chartName: string, build: () => any, assign: (chart: any) => void): void {
    try {
      this.chartConfigService.applyPieChart(
        chartName,
        build,
        (chart) => { assign(chart); this.changeDetectorRef.markForCheck(); },
        (loading) => { this.setChartLoading(chartName, loading); this.changeDetectorRef.markForCheck(); }
      );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  get rowColsClass(): string {
    try {
      const raw              =this.maxWidgetsPerRow;
      const widgetsPerRow    =(Number.isFinite(raw) && raw > 0) ? raw : 4;
      const safeWidgetsPerRow=Math.max(1, Math.min(6, widgetsPerRow, this.visibleChartCount));
  
      return `row-cols-1 row-cols-md-${safeWidgetsPerRow}`;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return "row-cols-1 row-cols-md-4";
    }
  }

  private allChartNames(): string[] {
    try {
      return [
        MdmDashboardWidgetTitles.devicesByManagementState,
        MdmDashboardWidgetTitles.devicesByManufacturer,
        MdmDashboardWidgetTitles.devicesByOperatingSystem,
        MdmDashboardWidgetTitles.devicesByOperatingSystemCompliant_Intune,
        MdmDashboardWidgetTitles.devicesByOperatingSystemCompliant_Ours,
        MdmDashboardWidgetTitles.devicesByOperatingSystemCompliant_OursMinor
      ];
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return [];
    }
  }

  public isChartLoading(chartName: string): boolean {
    try {
      return this.loadingCharts.has(chartName);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return false;
    }
  }

  private setChartLoading(chartName: string, loading: boolean): void {
    try {
      const next=new Set<string>(this.loadingCharts);

      if (loading)
        next.add(chartName);
      else
        next.delete(chartName);

      this.loadingCharts=next;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  //#region Legend collapse/expand

  public getLegendDisplayIndices(chart: WidgetRowChartType): number[] {
    try {
      const items: any[]=chart?.legendItems ?? [];

      return items.map((_, i) => i);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return [];
    }
  }

  public getOtherLegendItem(chart: WidgetRowChartType): { label: string; count: number } | null {
    try {
      return null;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return null;
    }
  }

  public trackLegendIndex(_index: number, value: number): number {
    try {
      return value;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return _index;
    }
  }

  public isLegendCollapsible(chart: WidgetRowChartType): boolean {
    try {
      const rawDataLength: number=<number>((chart as any)?._rawDataLength ?? (chart?.legendItems?.length ?? 0));

      return rawDataLength>this.legendCollapseThreshold;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return false;
    }
  }

  public isLegendShowingAll(chart: WidgetRowChartType): boolean {
    try {
      const chartName=chart?.chart?._chartName ?? "";
      return this.legendShowAllSet.has(chartName);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return false;
    }
  }

  public toggleLegendShowAll(chart: WidgetRowChartType): void {
    try {
      const chartName=chart?.chart?._chartName ?? "";

      if (!chartName)
        return;

      if (this.legendShowAllSet.has(chartName))
        this.legendShowAllSet.delete(chartName);
      else
        this.legendShowAllSet.add(chartName);

      const latestData=this.latestDataByChart.get(chartName);

      if (latestData) {
        const assign=this.getChartAssigner(chartName);

        if (assign)
          this.buildAndApplyPieChart(chartName, latestData, assign);
      }

      this.changeDetectorRef.markForCheck();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private getChartAssigner(chartName: string): ((chart: WidgetRowChartType) => void) | undefined {
    try {
      switch (chartName) {
        case MdmDashboardWidgetTitles.devicesByManagementState:
          return chart => this.devicesByManagementStateChart=chart;

        case MdmDashboardWidgetTitles.devicesByManufacturer:
          return chart => this.devicesByManufacturerChart=chart;

        case MdmDashboardWidgetTitles.devicesByOperatingSystem:
          return chart => this.devicesByOperatingSystemChart=chart;

        case MdmDashboardWidgetTitles.devicesByOperatingSystemCompliant_Intune:
          return chart => this.devicesByOperatingSystemCompliant_IntuneChart=chart;

        case MdmDashboardWidgetTitles.devicesByOperatingSystemCompliant_Ours:
          return chart => this.devicesByOperatingSystemCompliant_OursChart=chart;

        case MdmDashboardWidgetTitles.devicesByOperatingSystemCompliant_OursMinor:
          return chart => this.devicesByOperatingSystemCompliant_OursMinorChart=chart;

        default:
          return undefined;
      }
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return undefined;
    }
  }

  //#endregion

  public onLegendClick(chartRef: any, labelText: string): void {
    try {
      const chartName=chartRef?.opts?.chart?._chartName
                      ?? chartRef?._chartName
                      ?? chartRef?.chart?._chartName
                      ?? "unknown";

      if (labelText==ChartConfigService.OtherSliceRawLabel) {
        const chart        =this.getChartByName(chartName);
        const excludeLabels=<string[]>((chart as any)?._otherExcludeRawLabels ?? []);

        if (excludeLabels.length==0)
          return;

        this.legendClick.emit({ chartName, labelText: "Other", excludeLabels });
        return;
      }

      // alert(`WidgetRowMdmDevices01Component\nChart: ${chartName} | Label: ${labelText}`); return;

      this.legendClick.emit({chartName, labelText});
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private getChartByName(chartName: string): WidgetRowChartType | undefined {
    try {
      switch (chartName) {
        case MdmDashboardWidgetTitles.devicesByManagementState:
          return this.devicesByManagementStateChart;

        case MdmDashboardWidgetTitles.devicesByManufacturer:
          return this.devicesByManufacturerChart;

        case MdmDashboardWidgetTitles.devicesByOperatingSystem:
          return this.devicesByOperatingSystemChart;

        case MdmDashboardWidgetTitles.devicesByOperatingSystemCompliant_Intune:
          return this.devicesByOperatingSystemCompliant_IntuneChart;

        case MdmDashboardWidgetTitles.devicesByOperatingSystemCompliant_Ours:
          return this.devicesByOperatingSystemCompliant_OursChart;

        case MdmDashboardWidgetTitles.devicesByOperatingSystemCompliant_OursMinor:
          return this.devicesByOperatingSystemCompliant_OursMinorChart;

        default:
          return undefined;
      }
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return undefined;
    }
  }
  
  public actionButton0OnClick(): void {
    try {
      this.actionButton0Click.emit();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public toggleContainerExpansion(): void {
    try {
      this.expandContainer=!this.expandContainer;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  public collapseContainer(): void {
    try {
      this.expandContainer=false;
      this.changeDetectorRef.markForCheck();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  public closeContainer(): void {
    try {
      this.visibleContainer=false;
      this.containerClosed.emit();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public restoreContainer(): void {
    try {
      this.visibleContainer=true;
      this.changeDetectorRef.markForCheck();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
}
